import React from 'react';
import { Link } from 'react-router-dom';
import {
    Heart,
    Shield,
    CheckCircle2,
    ArrowRight,
    Stethoscope,
    Activity,
    FileText,
    Lock,
    Cloud,
    Monitor,
    Server
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernSector.scss';

const ModernHealth = () => {
    const stats = [
        { number: '100+', label: 'Healthcare Clients' },
        { number: '100%', label: 'HIPAA Compliant' },
        { number: '24/7', label: 'Support' },
        { number: '99.9%', label: 'System Uptime' }
    ];

    const services = [
        {
            icon: <Stethoscope size={32} />,
            title: 'Medical Equipment',
            description: 'Essential medical equipment and devices for healthcare facilities.',
            features: ['Diagnostic Equipment', 'Patient Monitors', 'Lab Instruments', 'Medical Supplies']
        },
        {
            icon: <Activity size={32} />,
            title: 'Telehealth Solutions',
            description: 'Comprehensive telehealth platforms for remote patient care.',
            features: ['Video Consultations', 'Remote Monitoring', 'Patient Portals', 'Mobile Apps']
        },
        {
            icon: <FileText size={32} />,
            title: 'EHR Integration',
            description: 'Seamless electronic health record integration and management.',
            features: ['EHR Systems', 'Data Migration', 'Interoperability', 'Analytics']
        },
        {
            icon: <Shield size={32} />,
            title: 'Healthcare Security',
            description: 'Comprehensive cybersecurity solutions for healthcare organizations.',
            features: ['HIPAA Compliance', 'Data Protection', 'Access Control', 'Threat Detection']
        }
    ];

    const capabilities = [
        'HIPAA-compliant solutions',
        'Healthcare-specific expertise',
        'FDA-regulated device support',
        'Clinical workflow optimization',
        'Patient data protection',
        'Healthcare interoperability'
    ];

    const techOfferings = [
        { name: 'Cloud Healthcare', icon: <Cloud size={24} /> },
        { name: 'Clinical Systems', icon: <Monitor size={24} /> },
        { name: 'Data Centers', icon: <Server size={24} /> },
        { name: 'Security', icon: <Lock size={24} /> }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Health Care"
                subtitle="Streamline Care with a Unified Digital Health Platform"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Health Care' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-sector-hero">
                <div className="jk-sector-hero__container">
                    <div className="jk-sector-hero__grid">
                        <div className="jk-sector-hero__content">
                            <span className="jk-section-label">
                                <Heart size={16} />
                                Healthcare Technology
                            </span>
                            <h2 className="jk-sector-hero__title">
                                Transforming
                                <span className="highlight"> Healthcare Delivery</span>
                            </h2>
                            <p className="jk-sector-hero__description">
                                We deliver healthcare technology solutions that improve patient outcomes
                                and operational efficiency. From medical equipment to telehealth platforms,
                                we help healthcare organizations embrace digital transformation while
                                maintaining the highest standards of security and compliance.
                            </p>
                            <div className="jk-sector-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Let's Connect
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    View Solutions
                                </Link>
                            </div>
                        </div>

                        <div className="jk-sector-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-sector-hero__stat">
                                    <span className="jk-sector-hero__stat-number">{stat.number}</span>
                                    <span className="jk-sector-hero__stat-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-sector-services">
                <div className="jk-sector-services__container">
                    <div className="jk-sector-services__header">
                        <span className="jk-section-label">
                            <Stethoscope size={16} />
                            Healthcare Solutions
                        </span>
                        <h2 className="jk-sector-services__title">
                            Comprehensive
                            <span className="highlight"> Healthcare IT</span>
                        </h2>
                        <p className="jk-sector-services__subtitle">
                            Technology solutions designed specifically for healthcare organizations
                        </p>
                    </div>

                    <div className="jk-sector-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-sector-services__card">
                                <div className="jk-sector-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-sector-services__card-title">{service.title}</h3>
                                <p className="jk-sector-services__card-description">{service.description}</p>
                                <ul className="jk-sector-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-sector-capabilities">
                <div className="jk-sector-capabilities__container">
                    <div className="jk-sector-capabilities__grid">
                        <div className="jk-sector-capabilities__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                Compliance & Security
                            </span>
                            <h2 className="jk-sector-capabilities__title">
                                HIPAA-Compliant
                                <span className="highlight"> Solutions</span>
                            </h2>
                            <p className="jk-sector-capabilities__text">
                                We understand the critical importance of patient data protection and
                                regulatory compliance in healthcare. All our solutions are designed to
                                meet or exceed HIPAA requirements and healthcare industry standards.
                            </p>

                            <ul className="jk-sector-capabilities__list">
                                {capabilities.map((item, index) => (
                                    <li key={index} className="jk-sector-capabilities__item">
                                        <CheckCircle2 size={20} className="jk-sector-capabilities__item-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Contact Our Healthcare Team
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-sector-capabilities__visual">
                            <div className="jk-sector-capabilities__tech-grid">
                                {techOfferings.map((tech, index) => (
                                    <div key={index} className="jk-sector-capabilities__tech-card">
                                        <div className="jk-sector-capabilities__tech-icon">
                                            {tech.icon}
                                        </div>
                                        <span className="jk-sector-capabilities__tech-name">{tech.name}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="jk-sector-compliance">
                                <h3 className="jk-sector-compliance__title">Healthcare Compliance</h3>
                                <p className="jk-sector-compliance__text">
                                    All solutions meet healthcare regulatory requirements
                                </p>
                                <ul className="jk-sector-compliance__list">
                                    <li><CheckCircle2 size={16} /> HIPAA Compliant</li>
                                    <li><CheckCircle2 size={16} /> HITECH Ready</li>
                                    <li><CheckCircle2 size={16} /> FDA Regulations</li>
                                    <li><CheckCircle2 size={16} /> HL7/FHIR Support</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernHealth;
